// UserCreate.js
import React, { useState, useRef } from 'react';
import { Form, Button, Card, Row, Col, InputGroup } from 'react-bootstrap';
import Select from 'react-select';
import { Eye, EyeSlash } from 'react-bootstrap-icons';

const UserCreate = ({organization_id, departments, onCreateUser, onCancel }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});

  const initialState = {
    organization_id: organization_id,
    firstname: '',
    lastname: '',
    email: '',
    phone: '',
    username: '',
    password: '',
    confirmPassword: '',
    roles: [],
    is_active: true,
    is_paid: false,
    trial_expired: false,
    is_enterprise: false
  };

  const [newUser, setNewUser] = useState(initialState);
  const password = useRef({});
  password.current = newUser.password;

  const roleOptions = [
    { value: 'user', label: 'User' },
    { value: 'admin', label: 'System Admin' },
    { value: 'org_admin', label: 'Enterprise Admin' }

  ];

  // Validation patterns
  const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  const phonePattern = /^(\+1|1)?[-.\s]?\(?[2-9]\d{2}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/;
  const passwordPattern = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
  const namePattern = /^[a-zA-Z\s\-]+$/;

  const selectStyles = {
    control: (base, state) => ({
      ...base,
      borderColor: state.isFocused ? '#86b7fe' : '#ced4da',
      boxShadow: state.isFocused ? '0 0 0 0.25rem rgba(13, 110, 253, 0.25)' : null,
      '&:hover': {
        borderColor: state.isFocused ? '#86b7fe' : '#ced4da'
      }
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: '#e9ecef'
    })
  };

  const validateField = (name, value) => {
    switch (name) {
      case 'firstname':
      case 'lastname':
        return value ? (namePattern.test(value) ? '' : 'No special characters allowed except hyphen') : 'This field is required';
      case 'email':
        return value ? (emailPattern.test(value) ? '' : 'Invalid email format') : 'Email is required';
      case 'phone':
        return value ? (phonePattern.test(value) ? '' : 'Invalid phone number format') : 'Phone number is required';
      case 'username':
        return value ? '' : 'Username is required';
      case 'password':
        return value ? (passwordPattern.test(value) ? '' : 'Password must be at least 8 characters long and include uppercase, lowercase, number, and special character') : 'Password is required';
      case 'confirmPassword':
        return value ? (value === password.current ? '' : 'Passwords do not match') : 'Confirm password is required';
      case 'roles':
        return value?.length > 0 ? '' : 'At least one role is required';
      default:
        return '';
    }
  };

  const handleChange = (e) => {
    const { name, type, checked, value } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    setNewUser(prev => ({ ...prev, [name]: newValue }));
    setErrors(prev => ({ ...prev, [name]: validateField(name, newValue) }));
  };

  const handleRolesChange = (selectedOptions) => {
    const roles = selectedOptions.map(option => option.value);
    setNewUser(prev => ({ ...prev, roles }));
    setErrors(prev => ({ ...prev, roles: validateField('roles', roles) }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate all fields
    const newErrors = {};
    Object.keys(newUser).forEach(key => {
      const error = validateField(key, newUser[key]);
      if (error) newErrors[key] = error;
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onCreateUser(newUser);
  };

  return (
    <Card>
      <Card.Body>
        <Form onSubmit={handleSubmit}>
          <Row>
            <Form.Group as={Col} lg="6" className="mb-3">
              <Form.Label>First Name*</Form.Label>
              <Form.Control
                type="text"
                name="firstname"
                value={newUser.firstname}
                onChange={handleChange}
                isInvalid={!!errors.firstname}
                placeholder="First Name"
              />
              <Form.Control.Feedback type="invalid">
                {errors.firstname}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group as={Col} lg="6" className="mb-3">
              <Form.Label>Last Name*</Form.Label>
              <Form.Control
                type="text"
                name="lastname"
                value={newUser.lastname}
                onChange={handleChange}
                isInvalid={!!errors.lastname}
                placeholder="Last Name"
              />
              <Form.Control.Feedback type="invalid">
                {errors.lastname}
              </Form.Control.Feedback>
            </Form.Group>
          </Row>

          <Row>
            <Form.Group as={Col} lg="6" className="mb-3">
              <Form.Label>Email*</Form.Label>
              <Form.Control
                type="email"
                name="email"
                value={newUser.email}
                onChange={handleChange}
                isInvalid={!!errors.email}
                placeholder="Email@domain.com"
              />
              <Form.Control.Feedback type="invalid">
                {errors.email}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group as={Col} lg="6" className="mb-3">
              <Form.Label>Phone*</Form.Label>
              <Form.Control
                type="tel"
                name="phone"
                value={newUser.phone}
                onChange={handleChange}
                isInvalid={!!errors.phone}
                placeholder="(123) 456-7890"
              />
              <Form.Control.Feedback type="invalid">
                {errors.phone}
              </Form.Control.Feedback>
            </Form.Group>
          </Row>
          <Row>
            <Form.Group className="mb-3" as={Col} lg="6">
              <Form.Label>Username*</Form.Label>
              <Form.Control
                type="text"
                name="username"
                value={newUser.username}
                onChange={handleChange}
                isInvalid={!!errors.username}
                placeholder="Username"
              />
              <Form.Control.Feedback type="invalid">
                {errors.username}
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-3" as={Col} lg="6">
              <Form.Label>Department</Form.Label>
              <Form.Select
                name="department"
                value={newUser.department}
                onChange={handleChange}
                style={{ fontSize: '0.8rem' }}
              >
                <option value="" style={{ fontSize: '0.8rem' }}>None</option>
                {departments.map(dept => (
                  <option key={dept.id} value={dept.id} style={{ fontSize: '0.8rem' }}>
                    {dept.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Row>
          <Row>
            <Form.Group as={Col} lg="6" className="mb-3">
              <Form.Label>Password*</Form.Label>
              <InputGroup>
                <Form.Control
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={newUser.password}
                  onChange={handleChange}
                  isInvalid={!!errors.password}
                  placeholder="Password"
                />
                <Button
                  variant="outline-secondary"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeSlash /> : <Eye />}
                </Button>
                <Form.Control.Feedback type="invalid">
                  {errors.password}
                </Form.Control.Feedback>
              </InputGroup>
            </Form.Group>

            <Form.Group as={Col} lg="6" className="mb-3">
              <Form.Label>Confirm Password*</Form.Label>
              <InputGroup>
                <Form.Control
                  type={showConfirmPassword ? "text" : "password"}
                  name="confirmPassword"
                  value={newUser.confirmPassword}
                  onChange={handleChange}
                  isInvalid={!!errors.confirmPassword}
                  placeholder="Confirm Password"
                />
                <Button
                  variant="outline-secondary"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <EyeSlash /> : <Eye />}
                </Button>
                <Form.Control.Feedback type="invalid">
                  {errors.confirmPassword}
                </Form.Control.Feedback>
              </InputGroup>
            </Form.Group>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Roles*</Form.Label>
            <Select
              isMulti
              name="roles"
              options={roleOptions}
              value={roleOptions.filter(option =>
                newUser.roles?.includes(option.value)
              )}
              onChange={handleRolesChange}
              styles={selectStyles}
              placeholder="Select roles..."
              isInvalid={!!errors.roles}
            />
            {errors.roles && (
              <div className="text-danger small mt-1">{errors.roles}</div>
            )}
          </Form.Group>

          <div className="d-flex gap-2 justify-content-end">
            <Button variant="secondary" onClick={onCancel}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Create User
            </Button>

          </div>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default UserCreate;